import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, from } from "rxjs";
import { filter, take, switchMap, tap, catchError, map } from "rxjs/operators";
import { StorageService } from "./storage.service";
import { HttpClient, HttpHeaders, HttpErrorResponse } from "@angular/common/http";
import { applicationClientId, endpointBase } from "@app/config/api.config";

/**
 * Logout actions para tracking detalhado no backend
 */
export type LogoutAction =
  | "USER" // Logout manual pelo usuário
  | "NO_REFRESH_TOKEN_INITIAL" // 401 e nunca teve refreshToken
  | "NO_REFRESH_TOKEN_LOST" // Tinha token mas foi perdido (memory/storage)
  | "REFRESH_FAILED_NETWORK" // Refresh falhou por erro de rede
  | "REFRESH_FAILED_INVALID" // Servidor rejeitou o refreshToken (401/403)
  | "REFRESH_FAILED_EXPIRED" // RefreshToken expirado
  | "REFRESH_FAILED_MISSING_RESPONSE" // Servidor não retornou novo token
  | "MAX_RETRIES_EXHAUSTED" // 3 tentativas falharam
  | "GRPC_AUTH_FAILED" // Erro de auth em chamada gRPC
  | "INVALID_AUTH_NO_PRODUCTS" // 401 ao carregar categorias/produtos
  | "SESSION_CONFLICT"; // Outra sessão invalidou esta

export interface LogoutContext {
  httpStatus?: number;
  errorMessage?: string;
  requestUrl?: string;
  hadRefreshToken?: boolean;
  refreshAttempts?: number;
  source?: "HTTP" | "GRPC";
  originalError?: any;
}

export interface TokenState {
  accessToken: string | null;
  refreshToken: string | null;
  isValid: boolean;
}

/**
 * TokenManagerService - Gerenciador centralizado de tokens
 *
 * Este serviço resolve os seguintes problemas:
 * 1. Race condition entre HTTP e gRPC fazendo refresh simultâneo
 * 2. Múltiplas instâncias de interceptor com estados diferentes
 * 3. Falta de logging detalhado para debug
 * 4. Validação inconsistente de tokens
 */
@Injectable({
  providedIn: "root",
})
export class TokenManagerService {
  // Estado dos tokens
  private _accessToken: string | null = null;
  private _refreshToken: string | null = null;

  // Controle de refresh centralizado
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  // Contador de tentativas de refresh
  private refreshAttempts = 0;
  private readonly MAX_REFRESH_RETRIES = 3;

  // Flag para saber se o usuário tinha token antes de perder
  private hadRefreshTokenBefore = false;

  // Timestamp do último refresh bem-sucedido
  private lastSuccessfulRefresh: Date | null = null;

  constructor(private storage: StorageService, private http: HttpClient) {
    this.log("TokenManagerService initialized");
  }

  // ============================================
  // GETTERS E SETTERS
  // ============================================

  get accessToken(): string | null {
    return this._accessToken;
  }

  set accessToken(token: string | null) {
    this._accessToken = token;
    this.log(`AccessToken ${token ? "set" : "cleared"}`);
  }

  get refreshToken(): string | null {
    return this._refreshToken;
  }

  set refreshToken(token: string | null) {
    if (token) {
      this.hadRefreshTokenBefore = true;
    }
    this._refreshToken = token;
    this.log(`RefreshToken ${token ? "set" : "cleared"}`);
  }

  get hasValidTokens(): boolean {
    return !!this._accessToken && !!this._refreshToken;
  }

  get tokenState(): TokenState {
    return {
      accessToken: this._accessToken,
      refreshToken: this._refreshToken,
      isValid: this.hasValidTokens,
    };
  }

  // ============================================
  // REFRESH TOKEN - CENTRALIZADO
  // ============================================

  /**
   * Executa refresh do token de forma centralizada.
   * Garante que apenas UM refresh aconteça por vez, mesmo com
   * múltiplas requisições HTTP e gRPC simultâneas.
   *
   * @param source - Origem da chamada (HTTP ou GRPC)
   * @returns Observable com o novo accessToken ou erro
   */
  refreshTokens(source: "HTTP" | "GRPC" = "HTTP"): Observable<{ status: boolean; accessToken?: string; action?: LogoutAction; context?: LogoutContext }> {
    this.log(`Refresh requested from ${source}`);

    // Verifica se tem refreshToken antes de tentar
    if (!this._refreshToken) {
      const action: LogoutAction = this.hadRefreshTokenBefore ? "NO_REFRESH_TOKEN_LOST" : "NO_REFRESH_TOKEN_INITIAL";

      const context: LogoutContext = {
        hadRefreshToken: this.hadRefreshTokenBefore,
        source,
        refreshAttempts: this.refreshAttempts,
      };

      this.logError(`No refresh token available`, context);

      return of({
        status: false,
        action,
        context,
      });
    }

    // Se já está fazendo refresh, aguarda o resultado
    if (this.isRefreshing) {
      this.log(`Refresh already in progress, waiting... (source: ${source})`);

      return this.refreshSubject.pipe(
        filter((token) => token !== null),
        take(1),
        map((accessToken) => ({
          status: true,
          accessToken: accessToken!,
        }))
      );
    }

    // Inicia novo refresh
    this.isRefreshing = true;
    this.refreshSubject.next(null);
    this.refreshAttempts++;

    this.log(`Starting refresh attempt ${this.refreshAttempts}/${this.MAX_REFRESH_RETRIES}`);

    return this.executeRefreshRequest().pipe(
      switchMap((response) => {
        if (response.status && response.accessToken) {
          // Sucesso
          this.isRefreshing = false;
          this.refreshAttempts = 0;
          this.lastSuccessfulRefresh = new Date();
          this.refreshSubject.next(response.accessToken);

          this.log(`Refresh successful, new token acquired`);

          return of({
            status: true,
            accessToken: response.accessToken,
          });
        } else {
          // Falha no refresh
          this.isRefreshing = false;

          const context: LogoutContext = {
            hadRefreshToken: true,
            source,
            refreshAttempts: this.refreshAttempts,
            errorMessage: response.errorMessage,
            httpStatus: response.httpStatus,
          };

          // Determina a action baseado no tipo de erro
          let action: LogoutAction = "REFRESH_FAILED_INVALID";

          if (response.httpStatus === 0 || !response.httpStatus) {
            action = "REFRESH_FAILED_NETWORK";
          } else if (response.httpStatus === 401 || response.httpStatus === 403) {
            action = "REFRESH_FAILED_EXPIRED";
          } else if (response.missingTokens) {
            action = "REFRESH_FAILED_MISSING_RESPONSE";
          }

          // Verifica se atingiu máximo de tentativas
          if (this.refreshAttempts >= this.MAX_REFRESH_RETRIES) {
            action = "MAX_RETRIES_EXHAUSTED";
            context.refreshAttempts = this.refreshAttempts;
          }

          this.logError(`Refresh failed`, context);

          return of({
            status: false,
            action,
            context,
          });
        }
      }),
      catchError((error) => {
        this.isRefreshing = false;

        const context: LogoutContext = {
          hadRefreshToken: true,
          source,
          refreshAttempts: this.refreshAttempts,
          originalError: error?.message || error,
        };

        this.logError(`Refresh exception`, context);

        return of({
          status: false,
          action: "REFRESH_FAILED_NETWORK" as LogoutAction,
          context,
        });
      })
    );
  }

  /**
   * Executa a chamada de refresh para o servidor
   */
  private executeRefreshRequest(): Observable<{
    status: boolean;
    accessToken?: string;
    refreshToken?: string;
    user?: any;
    errorMessage?: string;
    httpStatus?: number;
    missingTokens?: boolean;
  }> {
    const body = new URLSearchParams({
      refresh_token: this._refreshToken!,
      client_id: applicationClientId,
      grant_type: "refresh_token",
    }).toString();

    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });

    return this.http.post<any>(`${endpointBase}/auth`, body, { headers }).pipe(
      switchMap((response: any) => {
        const responseData = response?.data || response;

        if (!responseData?.accessToken || !responseData?.refreshToken) {
          this.logError(`Server response missing tokens`, {
            hasAccessToken: !!responseData?.accessToken,
            hasRefreshToken: !!responseData?.refreshToken,
          });

          return of({
            status: false,
            missingTokens: true,
            errorMessage: "Server did not return valid tokens",
            httpStatus: 200,
          });
        }

        this._accessToken = responseData.accessToken;
        this._refreshToken = responseData.refreshToken;

        // Preserva user existente se o backend não retornar no refresh (evita perda de sessão no cold start).
        return from(this.storage.get("auth")).pipe(
          switchMap((existingAuth) => {
            const user = responseData.user ?? existingAuth?.user;
            return from(
              this.storage.set("auth", {
                accessToken: responseData.accessToken,
                refreshToken: responseData.refreshToken,
                user,
              }),
            ).pipe(
              map(() => ({
                status: true,
                accessToken: responseData.accessToken,
                refreshToken: responseData.refreshToken,
                user,
              })),
            );
          }),
        );
      }),
      catchError((error: HttpErrorResponse) => {
        const httpStatus = error.status;
        const errorMessage = error.error?.message || error.message || "Unknown error";

        this.logError(`Refresh API error`, {
          httpStatus,
          errorMessage,
        });

        return of({
          status: false,
          errorMessage,
          httpStatus,
        });
      })
    );
  }

  // ============================================
  // UTILIDADES
  // ============================================

  /**
   * Reseta o estado do manager (chamado no logout)
   */
  reset(): void {
    this._accessToken = null;
    this._refreshToken = null;
    this.isRefreshing = false;
    this.refreshAttempts = 0;
    this.hadRefreshTokenBefore = false;
    this.lastSuccessfulRefresh = null;
    this.refreshSubject.next(null);

    this.log("TokenManager reset");
  }

  /**
   * Verifica se deve tentar refresh novamente
   */
  canRetryRefresh(): boolean {
    return this.refreshAttempts < this.MAX_REFRESH_RETRIES && !!this._refreshToken;
  }

  /**
   * Retorna contexto atual para logging
   */
  getContext(additionalContext?: Partial<LogoutContext>): LogoutContext {
    return {
      hadRefreshToken: this.hadRefreshTokenBefore,
      refreshAttempts: this.refreshAttempts,
      ...additionalContext,
    };
  }

  // ============================================
  // LOGGING
  // ============================================

  private log(_message: string, _data?: any): void {
    // Silent — logs removidos por não agregar valor em runtime
  }

  private logError(message: string, context?: any): void {
    const timestamp = new Date().toISOString();
    console.error(`[TokenManager ${timestamp}] ERROR: ${message}`, {
      ...context,
      tokenState: {
        hasAccessToken: !!this._accessToken,
        hasRefreshToken: !!this._refreshToken,
        hadRefreshTokenBefore: this.hadRefreshTokenBefore,
        refreshAttempts: this.refreshAttempts,
        lastSuccessfulRefresh: this.lastSuccessfulRefresh,
      },
    });
  }
}
